// ══════════════════════════════════════════════════════════════════════════════
// Aura Client, Frontend entry point to the Aura Orchestrator
// ══════════════════════════════════════════════════════════════════════════════
// All recommendation requests must go through this module.
// Returns the canonical AuraOrchestratorPayload, no custom shapes.
// ══════════════════════════════════════════════════════════════════════════════

import { recordAuraTouch } from './auraAttribution';

const ORCHESTRATOR_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/aura-orchestrator`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// ── Types (mirrored from edge function types.ts) ───────────────────────────────
// These are the subset the frontend actually consumes from the payload.

export interface AuraClientRequest {
  query: string;
  session_id?: string;
  turn_number?: number;
  context?: {
    previous_intent?: Record<string, unknown>;
    previous_listing_ids?: string[];
    previous_response_text?: string;
    consecutive_clarification_count?: number;
    // Architecture/28: echoed verbatim from the previous payload's active_comparison
    active_comparison?: unknown;
  };
  conversation_history?: Array<{ role: 'user' | 'assistant'; content: string }>;
  device_type?: 'mobile' | 'tablet' | 'desktop';
  anonymous_id?: string;
  wedding_context?: string;   // the active Wedding's state, primary input to reasoning (the bridge)
  wedding_id?: string;        // the active Wedding's id, joins reply telemetry to wedding actions (the recorder)
  wedding_destination?: string; // the active Wedding's destination, eligibility country when the query names no place
  debug?: boolean;            // when true, the response carries the reasoning trace (engineering-only)
}

export interface AuraClientListing {
  id: string;
  name: string;
  slug: string;
  category_slug: string;
  region: string | null;
  country: string | null;
  city: string | null;
  capacity_min: number | null;
  capacity_max: number | null;
  price_from: number | null;
  price_currency: string | null;
  hero_image_url: string | null;
  imgs_array?: string[];
  video_url?: string | null;
  short_description: string | null;
  luxury_tier: string | null;
  verified: boolean;
  is_editors_pick: boolean;
}

export interface AuraClientRecommendation {
  listing_id: string;
  rank: number;
  listing: AuraClientListing;
  reason: string;
  tags: string[];
  match_reasons?: string[]; // confirmed wedding-intelligence capabilities matching the brief
  scores: {
    final_aura_score: number;
    intent_match: number;
    capacity_fit: number;
    luxury_profile: number;
  };
}

export interface AuraClientResponse {
  session_id: string;
  query_id: string;
  response_text: string;
  follow_up_prompts: string[];
  recommendations: AuraClientRecommendation[];
  map_data: {
    pins: { listing_id: string; name: string; rank: number }[];
    region_slug: string | null;
    country: string | null;
  };
  intent: {
    category: string | null;
    region: string | null;
    country: string | null;
    city: string | null;
    capacity_min: number | null;
    capacity_max: number | null;
    styles: string[];
    requires_clarification: boolean;
    clarification_question: string | null;
    confidence: number;
    query_type: string;
  };
  zero_result: boolean;
  zero_result_reason: string | null;
  confidence: number;
  conversation_mode?: string;
  latency_ms: number;
  model_used: string;
  trace?: unknown[];          // reasoning trace, present only when the request set debug:true
  reasoning_id?: string;      // always present, searchable handle for this reasoning chain
  active_comparison?: unknown; // Architecture/28: echo back verbatim in context next turn
  product_context?: unknown;   // Shop surface: purchasable products (NOT listings)
  product_browse?: unknown;    // { label, link } for the browse-all row
}

export interface AuraClientError {
  error: string;
  zero_result: true;
  response_text: string;
}

// ── Main call ──────────────────────────────────────────────────────────────────

export async function queryAuraOrchestrator(
  request: AuraClientRequest,
): Promise<AuraClientResponse> {
  const res = await fetch(ORCHESTRATOR_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ANON_KEY}`,
    },
    body: JSON.stringify(request),
  });

  const data = await res.json();

  if (!res.ok) {
    const err = data as AuraClientError;
    throw new Error(err.error ?? `Orchestrator error ${res.status}`);
  }

  return data as AuraClientResponse;
}

// ── Click tracking ─────────────────────────────────────────────────────────────
// Fire-and-forget, never awaited by callers.

export function trackAuraClick(
  queryId: string,
  sessionId: string,
  listingId: string,
  rank: number,
  clickType: 'card' | 'profile' | 'enquire' | 'enquiry_started' | 'shortlist' | 'compare' | 'map_pin',
): void {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  if (!supabaseUrl) return;

  fetch(`${supabaseUrl}/rest/v1/aura_recommendation_clicks`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ANON_KEY}`,
      'apikey': ANON_KEY,
      'Prefer': 'return=minimal',
    },
    body: JSON.stringify({
      query_id: queryId,
      session_id: sessionId,
      listing_id: listingId,
      rank_at_click: rank,
      click_type: clickType,
    }),
  }).catch(() => {
    // Silently ignore, tracking failure must never surface to user
  });

  // Bridge the tab boundary. "View Profile" opens a new tab, and Aura's session id
  // is per-tab by contract, so without this the listing page has no idea Aura sent
  // the couple — which is why click_type='enquire' had never fired once. Recorded
  // here rather than at each call site so no future click surface can miss it.
  // 'enquire' is excluded: it is the conversion, and it must not attribute itself.
  if (clickType !== 'enquire' && clickType !== 'enquiry_started') recordAuraTouch(listingId, sessionId, queryId, rank);

  // Mirror the same event into the domain-agnostic taste substrate. The legacy
  // aura_recommendation_clicks table stays for existing admin dashboards; this is
  // the unified signal log every future entity type (vendor, dress, destination)
  // will also write into. One call here covers every existing click call site.
  // Implicit strength so the future taste engine can weight without re-deriving.
  const CLICK_WEIGHTS: Record<string, number> = {
    enquire: 3, enquiry_started: 2.5, shortlist: 2, compare: 1.5, profile: 1.2, card: 1, map_pin: 0.8,
  };
  trackTasteSignal('venue', listingId, clickType, {
    weight: CLICK_WEIGHTS[clickType] ?? 1,
    sessionId,
    queryId,
    context: { rank_at_click: rank, source: 'aura' },
  });
}

// ── Taste signals, domain-agnostic behavioural substrate ──────────────────────
// Append-only log feeding the Aura taste graph. Fire-and-forget, never awaited,
// never surfaces an error. Capture from day one (irreversible if skipped) even
// before anything reads it. entity_id is any entity's id as a string, venues
// today, vendors / dresses / destinations later, same table, no schema change.

export function trackTasteSignal(
  entityType: 'venue' | 'vendor' | 'dress' | 'destination' | 'experience',
  entityId: string,
  signalType: string,
  opts: {
    weight?: number;
    actorId?: string;
    sessionId?: string;
    queryId?: string;
    context?: Record<string, unknown>;
  } = {},
): void {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  if (!supabaseUrl || !entityId) return;

  fetch(`${supabaseUrl}/rest/v1/taste_signals`, {
    method: 'POST',
    // keepalive lets the request survive a navigation that happens on the same
    // click (e.g. the Similar Venues rail navigates immediately after firing),
    // without it the browser may abort the in-flight POST and the signal is lost.
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${ANON_KEY}`,
      'apikey': ANON_KEY,
      'Prefer': 'return=minimal',
    },
    body: JSON.stringify({
      entity_type: entityType,
      entity_id: entityId,
      signal_type: signalType,
      weight: opts.weight ?? 1.0,
      actor_id: opts.actorId ?? null,
      session_id: opts.sessionId ?? null,
      query_id: opts.queryId ?? null,
      context: opts.context ?? {},
    }),
  }).catch(() => {
    // Silently ignore, tracking failure must never surface to user
  });
}
